/** Pure display component for the newest-at-top fading message thread with action cards. */
import { AnimatePresence, motion } from 'motion/react';
import { getInitials } from '@formency/shared';
import { useAuth } from '@/shared/auth/AuthProvider';
import { ChatMessageText } from '@/shared/components/ChatMessageText';
import type { ActiveToolCall, ChatMessage, PendingSession, PendingJob } from './useCommandBarChat';
import {
  LogoAvatar,
  AssistantStreamingBody,
  SessionActionCard,
  JobStartedCard,
  SuggestionPills,
  stripModePrefix,
} from './CommandBarParts';

// ─── Props ────────────────────────────────────────────────────────────────────

interface CommandBarThreadProps {
  messages: ChatMessage[];
  isStreaming: boolean;
  pendingSession: PendingSession | null;
  pendingJob: PendingJob | null;
  suggestions: string[];
  activeToolCalls: ActiveToolCall[];
  onStartSession: () => void;
  onSendSuggestion: (text: string) => void;
}

// ─── Main component ───────────────────────────────────────────────────────────

export function CommandBarThread({
  messages,
  isStreaming,
  pendingSession,
  pendingJob,
  suggestions,
  activeToolCalls,
  onStartSession,
  onSendSuggestion,
}: CommandBarThreadProps) {
  const { user } = useAuth();

  // Use the shared helper so multi-word names get two initials ("NT") and
  // single-word names degrade gracefully ("M"). Fallback chain: name →
  // legacy firstName/lastName → email local-part → '?'.
  const userInitial = getInitials({
    name: user?.name ?? null,
    email: user?.email ?? null,
  });

  // Reverse so newest message is at top (index 0)
  const reversed = [...messages].reverse();

  // The streaming assistant message is the last one in the original array
  const lastAssistantId =
    messages.length > 0 && messages[messages.length - 1].role === 'assistant'
      ? messages[messages.length - 1].id
      : null;

  return (
    <div
      className="mx-auto w-full overflow-y-auto overflow-x-hidden"
      style={{
        height: 360,
        maxWidth: 820,
        maskImage: 'linear-gradient(to bottom, transparent, black 2%, black 85%, transparent 100%)',
        WebkitMaskImage:
          'linear-gradient(to bottom, transparent, black 2%, black 85%, transparent 100%)',
        scrollbarWidth: 'none',
      }}
    >
      <div className="relative px-3 pt-2 pb-16">
        {/* Messages rendered newest-at-top */}
        <div className="flex flex-col gap-3">
          {/* Suggestion pills — hidden during streaming or after job starts */}
          <AnimatePresence>
            {suggestions.length > 0 && !isStreaming && !pendingJob && (
              <SuggestionPills items={suggestions} onSelect={onSendSuggestion} />
            )}
          </AnimatePresence>

          {/* Action cards at the top (above newest message) */}
          <AnimatePresence>
            {pendingSession && !isStreaming && <SessionActionCard onStart={onStartSession} />}
            {pendingJob && <JobStartedCard />}
          </AnimatePresence>

          <AnimatePresence mode="popLayout">
            {reversed.map((msg) => (
              <motion.div
                key={msg.id}
                initial={{ opacity: 0, y: -10, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {msg.role === 'assistant' && (
                  <div className="flex max-w-[85%] gap-2.5">
                    <LogoAvatar />
                    <div
                      className="rounded-2xl rounded-tl-md px-4 py-2.5"
                      style={{
                        background: 'var(--color-surface-solid)',
                        border: '1px solid var(--color-border)',
                      }}
                    >
                      <div
                        className="leading-relaxed"
                        style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}
                      >
                        {msg.id === lastAssistantId && isStreaming ? (
                          <AssistantStreamingBody
                            text={msg.content}
                            activeToolCalls={activeToolCalls}
                          />
                        ) : (
                          <ChatMessageText text={msg.content} />
                        )}
                      </div>
                    </div>
                  </div>
                )}
                {msg.role === 'user' && (
                  <div className="flex max-w-[75%] flex-row-reverse gap-2.5">
                    <div
                      className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
                      style={{ background: 'var(--color-accent)' }}
                    >
                      <span
                        className="select-none font-semibold"
                        style={{ fontSize: '10px', color: 'var(--color-text-on-accent)' }}
                      >
                        {userInitial}
                      </span>
                    </div>
                    <div
                      className="rounded-2xl rounded-tr-md px-4 py-2.5"
                      style={{ background: 'var(--color-accent-subtle)' }}
                    >
                      <p
                        className="font-medium"
                        style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}
                      >
                        {stripModePrefix(msg.content)}
                      </p>
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
